// useCreditResetReminder — global "your monthly credits just reset" reminder.
//
// Fires exactly once per reset boundary (1st of the month at 00:00 UTC),
// shows an in-app toast for everyone signed in, and additionally fires a
// browser notification for users who already granted permission via the
// existing mention-notification flow in /settings.
//
// Idempotency: we persist the most recent reset moment (ISO string) we've
// shown the user under a per-user localStorage key, so they only see
// it once even if they reload, switch tabs, or come back after the page
// has been open across a month rollover.
//
// Side-effect: when we fire the alert, we also call `refresh()` on the
// credit-limit hook so the inline meter immediately shows the fresh
// `0 / limit` instead of a stale pre-reset number.
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useCreditLimit } from "@/hooks/useCreditLimit";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useTimezonePref, formatInTz } from "@/hooks/useTimezonePref";

const STORAGE_PREFIX = "hl_credit_reset_seen:"; // + userId → ISO string

/**
 * Most recent monthly reset moment. Reset is the 1st of the *current*
 * month at 00:00 UTC — once `now` is past that, we know the rollover
 * already happened. The next reset is the 1st of next month.
 */
const lastResetMomentMs = (now: Date): number =>
  Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0);

const readLastSeen = (userId: string): number | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
};

const writeLastSeen = (userId: string, momentMs: number) => {
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + userId,
      new Date(momentMs).toISOString(),
    );
  } catch {
    /* private mode / quota — best-effort only */
  }
};

export function useCreditResetReminder() {
  const { user } = useAuth();
  const { refresh: refreshCredit } = useCreditLimit();
  const { preferences } = useUserPreferences();
  const { tz } = useTimezonePref();

  // Track whether we've already fired in this tab so the polling effect
  // can short-circuit cheaply between checks.
  const firedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user?.id) {
      firedRef.current = null;
      return;
    }
    const userId = user.id;
    const browserNotifyOk =
      preferences?.mention_browser_notifications_enabled ?? false;

    const check = () => {
      const now = new Date();
      const currentResetMs = lastResetMomentMs(now);
      // Only act if (a) we're past the boundary, and (b) we haven't
      // already shown the user this exact boundary.
      if (now.getTime() < currentResetMs) return;
      if (firedRef.current === currentResetMs) return;
      const lastSeen = readLastSeen(userId);
      if (lastSeen !== null && lastSeen >= currentResetMs) {
        firedRef.current = currentResetMs;
        return;
      }

      firedRef.current = currentResetMs;
      writeLastSeen(userId, currentResetMs);

      // Refresh the credit balance so the meter reflects the rollover
      // immediately — otherwise the toast would land while the inline
      // chip still shows last month's number.
      void Promise.resolve(refreshCredit()).catch(() => {
        /* non-fatal — toast still fires */
      });

      const resetWhen = formatInTz(new Date(currentResetMs), tz, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      toast.success("Monthly credits refilled", {
        description: `Your credit balance reset on ${resetWhen}. Have at it.`,
        duration: 8000,
      });

      // Best-effort system notification — only when the user has the
      // mention-notification preference on AND the browser already
      // granted permission. We do NOT prompt here; that prompt lives in
      // /settings so it's a deliberate opt-in.
      if (
        browserNotifyOk &&
        typeof window !== "undefined" &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        try {
          const n = new Notification("HackerLabs · credits refilled", {
            body: `Your monthly credits just reset (${resetWhen}). You're back to full capacity.`,
            tag: "hl-credit-reset",
            // Reuse the site icon if available, falls back to default.
            icon: "/site.webmanifest",
          });
          // Auto-close after 12s — this is informational, not actionable.
          window.setTimeout(() => {
            try {
              n.close();
            } catch {
              /* noop */
            }
          }, 12_000);
        } catch {
          /* notification API can throw on some browsers/contexts */
        }
      }
    };

    // Check once on mount, then poll hourly. Hourly is enough granularity
    // for a once-a-month event and avoids any wake-up cost.
    check();
    const id = window.setInterval(check, 60 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [
    user?.id,
    preferences?.mention_browser_notifications_enabled,
    tz,
    refreshCredit,
  ]);
}
